import React, { ReactNode, useContext, useMemo } from "react";
import {
  SxProps,
  WhmAutocomplete,
  WhmBox,
  WhmTextField,
} from "@securitize/reactjs-whm";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
import TranslationKeys from "../../../contexts/I18nContext/translationKeys";
import { I18nContext } from "../../../contexts/I18nContext";
import { useCountries } from "./CountriesContext";
import { CountryListItem } from "../../../data/dtos/get-registration-countries.dto";

interface CountryTextFieldProps
  extends Omit<
    React.ComponentProps<
      typeof WhmAutocomplete<CountryListItem, false, false, false>
    >,
    | "options"
    | "value"
    | "getOptionLabel"
    | "isOptionEqualToValue"
    | "renderInput"
  > {
  name: string;
  value?: string;
  label: ReactNode;
  dataTestId?: string;
  fieldError?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  showFlag?: boolean;
  sx?: SxProps;
}

// Component that requires the context
const CountryTextField: React.FC<CountryTextFieldProps> = ({
  name,
  value: countryCode,
  label,
  dataTestId,
  disabled,
  fieldError,
  helperText,
  placeholder,
  showFlag = true,
  fullWidth = true,
  ...props
}) => {
  const { getTranslation } = useContext(I18nContext);
  const countriesQuery = useCountries();

  const countryValue = useMemo(
    () =>
      countryCode === undefined
        ? undefined
        : (countriesQuery.allowedCountries.find(
            (country) => country.value === countryCode,
          ) ?? null),
    [countryCode, countriesQuery.allowedCountries],
  );

  return (
    <WhmAutocomplete
      fullWidth={fullWidth}
      disabled={disabled || countriesQuery.isLoading}
      loading={countriesQuery.isLoading}
      value={countryValue}
      options={countriesQuery.allowedCountries}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionLabel={(option) => option.label}
      clearText={getTranslation(TranslationKeys.AUTOCOMPLETE_CLEAR)}
      openText={getTranslation(TranslationKeys.AUTOCOMPLETE_OPEN)}
      closeText={getTranslation(TranslationKeys.AUTOCOMPLETE_CLOSE)}
      renderInput={(params) => (
        <WhmTextField
          {...params}
          name={name}
          inputProps={{
            ...params.inputProps,
            "data-test-id": dataTestId,
          }}
          autoComplete="off"
          placeholder={placeholder}
          error={fieldError}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: showFlag && countryValue !== null && (
              <WhmBox sx={{ fontSize: "24px" }}>
                {countryCode ? getUnicodeFlagIcon(countryCode) : null}
              </WhmBox>
            ),
          }}
          label={label}
        />
      )}
      {...props}
    />
  );
};

export default CountryTextField;
